import React, { useContext, useEffect, useRef, useState } from 'react';
import cssClass from './NodeContentViewer.module.css';
import videoIcon from '../../assets/node-content-viewer/video.svg';
import articleIcon from '../../assets/node-content-viewer/article.svg';
import websiteIcon from '../../assets/node-content-viewer/website.svg';
import liveIcon from '../../assets/node-content-viewer/live.svg';
import mentorIcon from '../../assets/node-content-viewer/mentor.svg';
import collaborativeIcon from '../../assets/node-content-viewer/collaborative.svg';
import tickIcon from '../../assets/node-content-viewer/tick.svg';
import documentIcon from '../../assets/node-content-viewer/document.svg';
import databaseIcon from '../../assets/node-content-viewer/database.svg';
import folderIcon from '../../assets/node-content-viewer/folder.svg';
import assignmentIcon from '../../assets/assignment-Icon.svg';
import downloadIcon from '../../assets/node-content-viewer/download.svg';
import pendingIcon from '../../assets/learning-status-icons/pending.svg';
import completeIcon from '../../assets/learning-status-icons/complete.svg';
import inProgressIcon from '../../assets/learning-status-icons/in-progress.svg';
import dropdownIcon from '../../assets/select-element-dropdown-icon.svg';
import closeIcon from '../../assets/close.svg';
import lockIcon from '../../assets/node-content-viewer/lock.svg';
import unLockIcon from '../../assets/node-content-viewer/unlock.svg';
import { useNavigate, useParams } from 'react-router-dom';
import { UserContext } from '../../context/UserContext';
import { motion, AnimatePresence } from 'framer-motion';
import { ClientRoadmapContext } from '../../context/ClientRoadmapContext';
import Skeleton from '../roadmap-utility/Skeleton';
import { useNotification } from '../../hooks/useNotification';
import { v4 as uuidv4 } from 'uuid';

/**
 *
 * @param {Object} props - The props object.
 * @param {Object} props.selectedNode - The selected node object.
 * @param {Object} props.learningProgress - The learning progress object.
 * @param {Function} props.setLearningProgress - The function to set the learning progress.
 * @param {Function} props.closePopup - The function to close the popup.
 * @description This component is used to display the content of a selected node in the roadmap. It shows the title, description, and links associated with the node. It also allows the user to update their learning progress for the node.
 * @returns {JSX.Element} - The rendered component.
 */
const NodeContentViewer = ({
  selectedNode,
  learningProgress,
  setLearningProgress,
  closePopup,
  premiumAccessData,
  roadmapTitle,
  roadmapPrice,
}) => {
  // User details from UserContext
  const { userDetails } = useContext(UserContext);
  // State to checking we are making api call to fetch node's content
  const [isContentFetching, setIsContentFetching] = useState(false);
  // State to store node's content
  const [nodeContent, setNodeContent] = useState({});
  // Roadmap ID from the url path parameters
  const { roadmap_id } = useParams();
  // Navigation object for navigation
  const navigate = useNavigate();

  /**
   * useEffect hook to fetch the content of a selected roadmap node from the server.
   *
   * @effect
   * Triggers on every change to `selectedNode`.
   * Sends a GET request to the backend using `roadmap_id`, `selectedNode.id`, and `userDetails.email`.
   * Updates local state with the received node content (`title`, `description`, `links`).
   * Handles UI loading state via `setIsContentFetching`.
   *
   * @dependencies [selectedNode]
   *
   * @see setNodeContent - Updates the node content displayed to the user.
   * @see setIsContentFetching - Toggles loading state for UI feedback.
   * @see selectedNode - Node object selected by the user (must contain `id`).
   * @see roadmap_id - Identifier for the current roadmap context.
   * @see userDetails.email - User identity passed to the backend for personalized content retrieval.
   */
  useEffect(() => {
    const getNodeContent = async () => {
      try {
        if (selectedNode) {
          setIsContentFetching(true);
          const response = await fetch(
            `${process.env.REACT_APP_SERVER_URL}/r/get-node-content/${roadmap_id}/${selectedNode.id}?e=${userDetails?.email}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
              },
            },
          );
          const responseData = await response.json();
          if (response.status === 200) {
            setNodeContent({
              title: responseData?.content?.title || '',
              description: responseData?.content?.description || '',
              links: responseData?.content?.links || [],
            });
          }
        }
      } catch (error) {
      } finally {
        setIsContentFetching(false);
      }
    };
    getNodeContent();
  }, [selectedNode]);

  /**
   *
   * @param {String} content_type - It is the type of content, e.g. video, website, course
   * @returns icon according to the content type
   * @example
   * <img src={getContentImageIcon('video')} alt="" />
   */
  const getContentImageIcon = (content_type) => {
    switch (content_type.toLowerCase()) {
      case 'video':
        return videoIcon;
      case 'article':
        return documentIcon;
      case 'course':
        return liveIcon;
      case 'website':
        return websiteIcon;
      case 'opensource':
        return websiteIcon;
      case 'document':
        return documentIcon;
      case 'data_table':
        return databaseIcon;
      case 'folder':
        return folderIcon;
      case 'assignment':
        return assignmentIcon;
      case 'notebook':
        return articleIcon;
    }
  };

  /**
   * Fucntion to handle buy content button click
   * Not using right now but will be used later
   */
  const handleBuyContentButtonClick = () => {
    navigate(`/pay`, {
      state: {
        price: roadmapPrice,
        roadmap_id: roadmap_id,
        roadmap_title: roadmapTitle,
        redirectTo: window.location.pathname,
      },
    });
  };

  return (
    <div className={cssClass['node-content-viewer']}>
      {isContentFetching ? (
        <NodeContentViewerSkeletonLoader />
      ) : (
        <>
          <div className={cssClass['top']}>
            {/* <img src={setuLogo} alt="" /> */}
            {/* <img src={menuIcon} alt="" /> */}
            {['topic', 'subtopic', 'assignmentNode'].includes(
              selectedNode?.type,
            ) && (
              <ProgressStatusDropDown
                selectedNode={selectedNode}
                learningProgress={learningProgress}
                setLearningProgress={setLearningProgress}
                userDetails={userDetails}
                roadmapId={roadmap_id}
              />
            )}
            <button className={cssClass['close-button']} onClick={closePopup}>
              <img src={closeIcon} alt="" />
            </button>
          </div>

          <div className={cssClass['node-content-ct']}>
            <div className={cssClass['content-head']}>
              <p className={cssClass['node-title']}>
                {nodeContent?.title || 'No Title Provided'}
              </p>
              <p className={cssClass['node-description']}>
                {nodeContent?.description || 'No description provided.'}
              </p>
            </div>

            {['topic', 'subtopic', 'assignmentNode'].includes(
              selectedNode?.type,
            ) && (
              <>
                <div className={cssClass['open-access']}>
                  <div className={cssClass['open-access-heading']}>
                    <p className={cssClass['open-access-p']}>Open Access</p>
                    <div className={cssClass['open-access-hr']}></div>
                  </div>

                  <div className={cssClass['open-access-contents']}>
                    {nodeContent?.links &&
                      (nodeContent?.links.filter(
                        (link) => link.accessType === 'public',
                      ).length === 0 ? (
                        <div className={cssClass['node-description']}>
                          {' '}
                          <p>No content available</p>{' '}
                        </div>
                      ) : (
                        nodeContent?.links
                          .filter((link) => link.accessType === 'public')
                          .map(
                            (link, index) =>
                              link?.accessType === 'public' && (
                                <div className={cssClass['contents-ct']}>
                                  <div
                                    className={cssClass['contents']}
                                    key={index}
                                  >
                                    <img
                                      src={getContentImageIcon(link?.type)}
                                      alt=""
                                    />
                                    <a href={link?.url} target="_blank">
                                      {link?.title || 'No title Provided'}{' '}
                                    </a>
                                  </div>
                                  {link?.downloadable && (
                                    <img
                                      src={downloadIcon}
                                      alt="downloadable"
                                      title="downloadable"
                                    />
                                  )}
                                </div>
                              ),
                          )
                      ))}
                  </div>
                </div>

                <div
                  className={`${cssClass['premium-access']} ${!premiumAccessData ? cssClass['no-premium-access'] : ''}`}
                >
                  <div className={cssClass['premium-access-heading']}>
                    <p className={cssClass['premium-access-p']}>
                      Premium Access
                    </p>
                    <hr className={cssClass['premium-access-hr']} />
                    <div className={cssClass['lock-unlock-ct']}>
                      <img
                        src={!premiumAccessData ? lockIcon : unLockIcon}
                        alt=""
                      />
                    </div>
                  </div>

                  <div className={cssClass['premium-access-contents']}>
                    <div className={cssClass['setu-dap']}>
                      {/* <div className={cssClass['setu-dap-heading']}>
                    <p>SETU DAP Academy</p>
                  </div> */}

                      <div className={cssClass['setu-dap-contents']}>
                        {nodeContent?.links &&
                          (nodeContent?.links.filter(
                            (link) => link.accessType === 'premium',
                          ).length === 0 ? (
                            <div className={cssClass['node-description']}>
                              <p>No content available</p>
                            </div>
                          ) : (
                            nodeContent?.links
                              .filter((link) => link.accessType === 'premium')
                              .map((link, index) => (
                                <div className={cssClass['contents-ct']}>
                                  <div
                                    className={cssClass['contents']}
                                    key={index}
                                  >
                                    <img
                                      src={getContentImageIcon(link?.type)}
                                      alt=""
                                    />
                                    <a href={link?.url} target="_blank">
                                      {link?.title || 'No title Provided'}
                                    </a>
                                  </div>
                                  {link?.downloadable && (
                                    <img
                                      src={downloadIcon}
                                      alt="downloadable"
                                      title="downloadable"
                                    />
                                  )}
                                </div>
                              ))
                          ))}

                        {/* <div className={cssClass["contents"]}>
                      <img src={articleIcon} alt="" />
                      <div className={cssClass["with-done-icon"]}>
                        <a href="">Components of writing a good prompt <img src={tickIcon} alt="" /></a>
                      </div>
                    </div>
                    <div className={cssClass["contents"]}>
                      <img src={videoIcon} alt="" />
                      <a href="">Zero-shot prompting</a>
                    </div>
                    <div className={cssClass["contents"]}>
                      <img src={articleIcon} alt="" />
                      <a href="">Meta prompting</a>
                    </div>
                    <div className={cssClass["contents"]}>
                      <img src={articleIcon} alt="" />
                      <a href="">In-context learning</a>
                    </div>
                    <div className={cssClass["contents"]}>
                      <img src={websiteIcon} alt="" />
                      <div className={cssClass["with-button"]}>
                        <p>Hands-on Project</p>
                        <button>Start</button>
                      </div>
                    </div>
                    <div className={cssClass["contents-live"]}>
                      <img src={liveIcon} alt="" />
                      <div className={cssClass["with-button-and-date"]}>
                        <div className={cssClass["with-date"]}>
                          <p className={cssClass["with-date-p"]}>Prompt Decomposition</p>
                          <p className={cssClass["date"]}>Scheduled for 19 January 2024</p>
                        </div>
                        <button>Start</button>
                      </div>
                    </div>
                    <div className={cssClass["contents-live"]}>
                      <img src={liveIcon} alt="" />
                      <div className={cssClass["with-date"]}>
                        <p className={cssClass["with-date-p"]}>Prompt Decomposition</p>
                        <p className={cssClass["date"]}>Scheduled for 19 January 2024</p>
                      </div>
                    </div>
                    <div className={cssClass["contents-live"]}>
                      <img src={liveIcon} alt="" />
                      <div className={cssClass["with-date"]}>
                        <p className={cssClass["with-date-p"]}>Prompt Ensembling</p>
                        <p className={cssClass["date"]}>Scheduled for 19 January 2024</p>
                      </div>
                    </div>
                    <div className={cssClass["contents"]}>
                      <img src={websiteIcon} alt="" />
                      <div className={cssClass["with-button"]}>
                        <p>Co-Create a project</p>
                        <button>Start</button>
                      </div>
                    </div>
                    <div className={cssClass["contents"]}>
                      <img src={websiteIcon} alt="" />
                      <div className={cssClass["with-button"]}>
                        <p>Create a prototype</p>
                        <button>Start</button>
                      </div>
                    </div>
                    <div className={cssClass["contents-without-img"]}>
                      <div className={cssClass["with-button"]}>
                        <p>Assignment</p>
                        <button>Start</button>
                      </div>
                    </div>
                    <div className={cssClass["contents"]}>
                      <img src={mentorIcon} alt="" />
                      <div className={cssClass["with-button"]}>
                        <p>Mentoriship</p>
                        <button>Schedule</button>
                      </div>
                    </div>
                    <div className={cssClass["contents"]}>
                      <img src={collaborativeIcon} alt="" />
                      <div className={cssClass["with-button"]}>
                        <p>Collaborate</p>
                        <button>Join Room</button>
                      </div>
                    </div> */}
                      </div>
                    </div>

                    {/* <div className={cssClass["dap-partners-hr"]}></div>

                <div className={cssClass["setu-partners"]}>
                  <div className={cssClass["setu-partners-heading"]}>
                    <p>SETU Partners</p>
                  </div>

                  <div className={cssClass["setu-partners-contents"]}>
                    <div className={cssClass["contents"]}>
                      <img src={videoIcon} alt="" />
                      <a href="">Few shot prompting using Exemplars</a>
                    </div>
                    <div className={cssClass["contents-live"]}>
                      <img src={liveIcon} alt="" />
                      <div className={cssClass["with-button-and-date"]}>
                        <div className={cssClass["with-date"]}>
                          <p className={cssClass["with-date-p"]}>Prompt Response Engineering</p>
                          <p className={cssClass["date"]}>Scheduled for 29 January 2024</p>
                        </div>
                        <button>Register</button>
                      </div>
                    </div>
                    <div className={cssClass["contents"]}>
                      <img src={articleIcon} alt="" />
                      <div className={cssClass["with-done-icon"]}>
                        <a href="">Prompt Engineering using Self-criticism 
                          <img src={tickIcon} alt="" />
                          </a>
                      </div>
                    </div>
                  </div>
                </div> */}
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/**
 *
 * @param {Object} props - The props object.
 * @param {Object} props.selectedNode - The selected node object.
 * @param {Object} props.learningProgress - The learning progress object.
 * @param {Function} props.setLearningProgress - The function to set the learning progress.
 * @param {Function} props.userDetails - The object of user details which @see Usercontext.jsx.
 * @description This component is used to display the status of the current learning status of that node. User can update it from a dropdow. It has three options - pending, in progress and done.
 * @returns {JSX.Element} - The rendered component.
 */
const ProgressStatusDropDown = ({
  selectedNode,
  learningProgress,
  setLearningProgress,
  userDetails,
  roadmapId,
}) => {
  // List of progress options
  const progressStatusList = [
    { value: 'pending', label: 'Pending', icon: pendingIcon },
    { value: 'in-progress', label: 'In Progress', icon: inProgressIcon },
    { value: 'done', label: 'Done', icon: completeIcon },
  ];

  // Animation variants
  const dropdownVariants = {
    hidden: {
      opacity: 0,
      height: 0,
      transition: {
        duration: 0.2,
        ease: 'easeInOut',
      },
    },
    visible: {
      opacity: 1,
      height: 'auto',
      transition: {
        duration: 0.3,
        ease: 'easeOut',
      },
    },
  };

  const arrowVariants = {
    open: { rotate: 180 },
    closed: { rotate: 0 },
  };

  // Context to set the count of completed content from api call. Server will seeeeeend the completed content count after a node marked as complete
  const { setContentCompleted } = useContext(ClientRoadmapContext);
  // State to check dropdown open or not
  const [isOpen, setIsOpen] = useState(false);
  // Current progress status
  const [progressStatus, setProgressStatus] = useState(progressStatusList[0]);
  // Reference to progress dropdown
  const progressStatusDropdownRef = useRef(null);
  // Functions to show notifications.
  const { showLoadingNotification, resolveNotification, rejectNotification } =
    useNotification();

  /**
   *
   * @param {String} nodeId Selected node's id
   * @param {Object} status Learning status to be updated
   * @description This function updates the learning status of a node in the server. It sends a PATCH request to the server with the updated status.
   */
  const updateLearningStatus = async (nodeId, status) => {
    const oldStatus = progressStatus;
    const newStatus = status.value;
    const data = {
      progress: {
        id: nodeId,
        status: newStatus,
      },
    };
    setProgressStatus(status);
    setIsOpen(false);

    const toastId = uuidv4();
    showLoadingNotification(toastId, 'Updating learning status');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/r/update-learning-status?e=${userDetails?.email}&r_id=${roadmapId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        },
      );
      const responseData = await response.json();
      if (response.status === 200) {
        // setProgressStatus(status);
        const currentTime = new Date().toISOString();
        setLearningProgress((prev) => ({
          ...prev,
          [selectedNode?.id]: {
            ...(prev[selectedNode?.id] || { started_at: currentTime }),
            status: newStatus,
            updated_at: currentTime,
          },
        }));
        setContentCompleted(responseData?.content_completed);
        resolveNotification(toastId, 'Status updated successfully');
      } else {
        rejectNotification(toastId, 'Something went wrong. Please try again');
        setProgressStatus(oldStatus);
      }
    } catch (error) {
      console.log(error);
      setProgressStatus(oldStatus);
      rejectNotification(toastId, 'Something went wrong. Please try again');
    }
  };

  // Function to open and close fropdown
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  // useEffect to set learning status when component first renders
  useEffect(() => {
    const learningStatus =
      learningProgress[selectedNode?.id]?.status || 'pending';
    setProgressStatus(
      progressStatusList.find((ps) => ps.value === learningStatus),
    );
  }, []);

  // It adds event listner to the dropdown to close when click outside of dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        progressStatusDropdownRef.current &&
        !progressStatusDropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div
      className={cssClass['dropdown-container']}
      ref={progressStatusDropdownRef}
    >
      <div
        className={`${cssClass['dropdown-header']} ${isOpen ? cssClass['dropdown-open'] : ''}`}
        onClick={toggleDropdown}
      >
        <div className={cssClass['selected-option']}>
          <div className={cssClass['progress-st-ct']}>
            <img src={progressStatus?.icon} alt="" />
            <span className={cssClass['progress-label']}>
              {progressStatus?.label}
            </span>
          </div>
          <div className={cssClass['update-status']}>
            <span className={cssClass['update-status-text']}>
              Update Status
            </span>
            <motion.img
              src={dropdownIcon}
              alt=""
              animate={isOpen ? 'open' : 'closed'}
              variants={arrowVariants}
              transition={{ duration: 0.2 }}
            />
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className={cssClass['dropdown-options']}
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={dropdownVariants}
          >
            {progressStatusList
              .filter((option) => option.value !== progressStatus.value)
              .map((option) => (
                <div
                  key={option.value}
                  className={cssClass['dropdown-option']}
                  onClick={() => updateLearningStatus(selectedNode?.id, option)}
                  transition={{ duration: 0.1 }}
                >
                  <div className={cssClass['progress-st-ct']}>
                    <img src={option?.icon} alt="" />
                    <span className={cssClass['progress-label']}>
                      {option?.label}
                    </span>
                  </div>
                </div>
              ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const NodeContentViewerSkeletonLoader = () => {
  return (
    <>
      <div className={cssClass['top']}>
        <Skeleton height={43} width={254} />
        <Skeleton height={34} width={34} />
      </div>
      <div className={cssClass['node-content-ct']}>
        <div className={cssClass['content-head']}>
          <Skeleton height={64} width={'100%'} />
          <Skeleton height={10} width={'100%'} count={5} gap={1} />
        </div>
        <div className={cssClass['open-access']}>
          <div className={cssClass['open-access-heading']}>
            <Skeleton height={33} width={120} />
            <Skeleton height={2} width={'100%'} />
          </div>
          <div className={cssClass['open-access-contents']}>
            {Array(5)
              .fill(0)
              .map((_, index) => (
                <div key={index} className={cssClass['contents']}>
                  <Skeleton variant="avatar" />
                  <Skeleton height={30} width={'100%'} />
                </div>
              ))}
          </div>
        </div>
        <div className={cssClass['open-access']}>
          <div className={cssClass['open-access-heading']}>
            <Skeleton height={33} width={120} />
            <Skeleton height={2} width={'100%'} />
          </div>
          <div className={cssClass['open-access-contents']}>
            {Array(5)
              .fill(0)
              .map((_, index) => (
                <div key={index} className={cssClass['contents']}>
                  <Skeleton variant="avatar" />
                  <Skeleton height={30} width={'100%'} />
                </div>
              ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default NodeContentViewer;
